/**
 * LeadChecklist — stage-aware checklist for the lead playbook (V1).
 *
 * Persists per-item completion to Mongo via PATCH /leads/:id/checklist.
 * Keeps prior-stage completions; never resets on stage change.
 * When an item with `suggestsNext` is toggled to done, shows an inline
 * suggestion that the user can apply (writes through next_action /
 * next_action_date) or dismiss locally.
 */
import React, { useMemo, useState } from "react";
import { CheckCircle, Circle, CaretDown, CaretRight } from "@phosphor-icons/react";
import { api, formatError } from "@/lib/api";
import { normalizeStatus } from "@/lib/leadStatuses";
import {
  CHECKLIST_BY_STAGE, STAGE_LABEL, STAGE_ORDER,
  findChecklistItem, offsetIsoDate,
} from "@/lib/leadPlaybook";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" });
  } catch { return iso; }
}

export default function LeadChecklist({ lead, onChanged }) {
  const stage = normalizeStatus(lead?.status) || "new";
  const items = CHECKLIST_BY_STAGE[stage] || [];
  const checklist = lead?.checklist || {};
  const [busyKey, setBusyKey] = useState(null);
  const [error, setError] = useState("");
  const [suggestion, setSuggestion] = useState(null); // { itemKey, action, date }
  const [confirmReplace, setConfirmReplace] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const priorStages = useMemo(() => {
    return STAGE_ORDER
      .filter(s => s !== stage)
      .map(s => ({ stage: s, items: Object.entries(checklist[s] || {}).filter(([, v]) => v?.done) }))
      .filter(g => g.items.length > 0);
  }, [checklist, stage]);

  const toggle = async (item) => {
    const current = !!checklist?.[stage]?.[item.key]?.done;
    const next = !current;
    setBusyKey(item.key); setError("");
    try {
      const { data } = await api.patch(`/leads/${lead.lead_id}/checklist`, {
        stage, item_key: item.key, done: next,
      });
      onChanged?.(data);
      if (next && item.suggestsNext) {
        setSuggestion({
          itemKey: item.key,
          action: item.suggestsNext.action,
          date: offsetIsoDate(item.suggestsNext.offsetDays),
        });
        setConfirmReplace(false);
      }
    } catch (e) { setError(formatError(e)); }
    finally { setBusyKey(null); }
  };

  const applySuggestion = async () => {
    if (!suggestion) return;
    const hasExisting = !!(lead?.next_action || lead?.next_action_date);
    if (hasExisting && !confirmReplace) {
      setConfirmReplace(true);
      return;
    }
    setBusyKey("__suggestion__"); setError("");
    try {
      const { data } = await api.patch(`/leads/${lead.lead_id}`, {
        next_action: suggestion.action,
        next_action_date: suggestion.date,
      });
      onChanged?.(data);
      setSuggestion(null);
      setConfirmReplace(false);
    } catch (e) { setError(formatError(e)); }
    finally { setBusyKey(null); }
  };

  if (items.length === 0) {
    return <div className="text-xs text-stone-400 italic">No hay pasos definidos para esta etapa.</div>;
  }

  return (
    <div className="space-y-2">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded p-2">{error}</div>
      )}
      <ul className="space-y-1.5" data-testid="lead-checklist">
        {items.map(item => {
          const state = checklist?.[stage]?.[item.key];
          const done = !!state?.done;
          const busy = busyKey === item.key;
          return (
            <li key={item.key}>
              <button
                onClick={() => toggle(item)}
                disabled={busy}
                data-testid={`lead-checklist-${item.key}`}
                className={`w-full flex items-start gap-2 text-left text-sm px-2 py-1.5 rounded border transition ${
                  done
                    ? "border-emerald-200 bg-emerald-50/50 text-stone-700"
                    : "border-stone-200 bg-white hover:border-stone-900"
                } disabled:opacity-50`}
              >
                {done
                  ? <CheckCircle size={16} weight="fill" className="mt-0.5 text-emerald-600 shrink-0" />
                  : <Circle size={16} weight="regular" className="mt-0.5 text-stone-400 shrink-0" />}
                <span className={done ? "line-through decoration-stone-400" : ""}>{item.label}</span>
              </button>
            </li>
          );
        })}
      </ul>

      {suggestion && (
        <div
          data-testid="lead-checklist-suggestion"
          className="rounded border border-amber-200 bg-amber-50 p-2.5 text-xs space-y-1.5"
        >
          <div className="text-amber-900">
            Sugerencia de próxima acción: <span className="font-medium">{suggestion.action}</span>
            {" · "}{fmtDate(suggestion.date)}
          </div>
          {confirmReplace && (
            <div className="text-amber-800">
              Ya hay una próxima acción registrada. ¿Reemplazarla?
            </div>
          )}
          <div className="flex gap-1.5">
            <button
              onClick={applySuggestion}
              disabled={busyKey === "__suggestion__"}
              data-testid="lead-checklist-suggestion-apply"
              className="px-2 py-0.5 rounded bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-50"
            >
              {confirmReplace ? "Sí, reemplazar" : "Aplicar"}
            </button>
            <button
              onClick={() => { setSuggestion(null); setConfirmReplace(false); }}
              className="px-2 py-0.5 rounded border border-stone-300 text-stone-700 hover:bg-stone-100"
            >
              Descartar
            </button>
          </div>
        </div>
      )}

      {priorStages.length > 0 && (
        <div className="pt-1">
          <button
            type="button"
            onClick={() => setShowHistory(v => !v)}
            data-testid="lead-checklist-history-toggle"
            className="inline-flex items-center gap-1 text-[11px] text-stone-500 hover:text-stone-800"
          >
            {showHistory ? <CaretDown size={11} /> : <CaretRight size={11} />}
            Pasos anteriores ({priorStages.reduce((n, g) => n + g.items.length, 0)})
          </button>
          {showHistory && (
            <div className="mt-1.5 space-y-1.5">
              {priorStages.map(g => (
                <div key={g.stage} className="text-xs">
                  <div className="text-[10px] uppercase tracking-widest text-stone-400 mb-0.5">
                    {STAGE_LABEL[g.stage] || g.stage}
                  </div>
                  <ul className="space-y-0.5">
                    {g.items.map(([key]) => {
                      const meta = findChecklistItem(g.stage, key);
                      return (
                        <li key={key} className="flex items-center gap-1.5 text-stone-600">
                          <CheckCircle size={11} weight="fill" className="text-emerald-500" />
                          <span className="line-through decoration-stone-300">{meta?.label || key}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
